import { CUSTOM_FIELDS_VALUE_SOURCE } from 'app/shared/components/customFields/CustomFieldsSection/CustomFieldsSection.constants';
import _ from 'lodash';

import {
  CUSTOM_FIELD_TARGET,
  CUSTOM_FIELD_TARGET_GROUP_LABEL,
  CUSTOM_FIELD_TARGET_ORDER,
} from 'constants/customFieldTargets';
import { getLiabilityOption, getPersonFullName } from 'utils/loanApplication';

const MISSING_LABEL_FALLBACK = 'N/A';

const getBorrowerLabel = borrower => getPersonFullName(borrower) || MISSING_LABEL_FALLBACK;

const getLiabilityLabel = liability =>
  getLiabilityOption(liability)?.name ?? MISSING_LABEL_FALLBACK;

const getNbeLabel = nbe => nbe?.fullName || getPersonFullName(nbe) || MISSING_LABEL_FALLBACK;

const formatPropertyAddress = address => {
  if (!address) {
    return '';
  }

  const street = _.compact([address.line1, address.line2]).join(' ');
  const city = address.city ?? '';
  const state = address.state ?? '';
  const zip = address.postalCode ?? address.zip ?? '';
  const tail = _.compact([city, state]).join(', ');
  const cityStateZip = _.compact([tail, zip]).join(' ');

  return _.compact([street, cityStateZip]).join(', ');
};

const getPropertyAddress = property => formatPropertyAddress(property?.address);

const isSelectedManualEnabled = (definition, selectedIds) =>
  !!definition?.isEnabled &&
  definition?.valueSource?.type === CUSTOM_FIELDS_VALUE_SOURCE.MANUAL &&
  selectedIds.includes(definition?.id);

const filterSelectedManualEnabledDefinitions = ({ definitions, selectedIds }) =>
  _.filter(definitions, definition => isSelectedManualEnabled(definition, selectedIds));

const groupDefinitionsByTarget = definitions =>
  _.groupBy(
    _.filter(definitions, definition => !_.isNil(definition?.target)),
    'target',
  );

const buildBorrowerSections = ({ target, definitions, borrowers }) =>
  _.map(borrowers, borrower => ({
    key: `${target}:${borrower.borrowerId}`,
    target,
    entityId: borrower.borrowerId,
    subHeader: getBorrowerLabel(borrower),
    definitions,
  }));

const buildPropertySections = ({ target, definitions, properties }) =>
  _.map(properties, property => ({
    key: `${target}:${property.id ?? property.propertyId}`,
    target,
    entityId: property.id ?? property.propertyId,
    subHeader: getPropertyAddress(property) || MISSING_LABEL_FALLBACK,
    definitions,
  }));

const buildIncomeSections = ({ target, definitions, incomes }) =>
  _.map(incomes, income => ({
    key: `${target}:${income.incomeId}`,
    target,
    entityId: income.incomeId,
    subHeader: income.typeAndAmount ?? MISSING_LABEL_FALLBACK,
    definitions,
  }));

const buildAssetSections = ({ target, definitions, assets }) =>
  _.map(assets, asset => ({
    key: `${target}:${asset.assetId ?? asset.id}`,
    target,
    entityId: asset.assetId ?? asset.id,
    subHeader: asset.typeAndAmount ?? MISSING_LABEL_FALLBACK,
    definitions,
  }));

const buildLiabilitySections = ({ target, definitions, liabilities }) =>
  _.map(liabilities, liability => ({
    key: `${target}:${liability.liabilityId ?? liability.id}`,
    target,
    entityId: liability.liabilityId ?? liability.id,
    subHeader: getLiabilityLabel(liability),
    definitions,
  }));

const buildNonBorrowerEntitySections = ({ target, definitions, nbes }) =>
  _.map(nbes, nbe => ({
    key: `${target}:${nbe.nonBorrowerEntityId}`,
    target,
    entityId: nbe.nonBorrowerEntityId,
    subHeader: getNbeLabel(nbe),
    definitions,
  }));

const SECTION_BUILDER_BY_TARGET = {
  [CUSTOM_FIELD_TARGET.LOAN]: ({ target, definitions, applicationId }) => [
    {
      key: `${target}:${applicationId}`,
      target,
      entityId: applicationId,
      subHeader: null,
      definitions,
    },
  ],
  [CUSTOM_FIELD_TARGET.SUBJECT_PROPERTY]: ({ target, definitions, subjectProperty }) => {
    if (!subjectProperty?.propertyId) {
      return [];
    }

    return [
      {
        key: `${target}:${subjectProperty.propertyId}`,
        target,
        entityId: subjectProperty.propertyId,
        subHeader: null,
        definitions,
      },
    ];
  },
  [CUSTOM_FIELD_TARGET.BORROWER]: buildBorrowerSections,
  [CUSTOM_FIELD_TARGET.PROPERTY]: buildPropertySections,
  [CUSTOM_FIELD_TARGET.INCOME]: buildIncomeSections,
  [CUSTOM_FIELD_TARGET.ASSET]: buildAssetSections,
  [CUSTOM_FIELD_TARGET.LIABILITY]: buildLiabilitySections,
  [CUSTOM_FIELD_TARGET.NON_BORROWER_ENTITY]: buildNonBorrowerEntitySections,
};

export const buildModalSectionGroups = ({
  definitions,
  selectedIds,
  applicationId,
  subjectProperty,
  borrowers,
  properties,
  incomes,
  assets,
  liabilities,
  nbes,
}) => {
  const selectedDefinitions = filterSelectedManualEnabledDefinitions({ definitions, selectedIds });
  const definitionsByTarget = groupDefinitionsByTarget(selectedDefinitions);

  return _.flatMap(CUSTOM_FIELD_TARGET_ORDER, target => {
    const targetDefinitions = definitionsByTarget[target];
    const builder = SECTION_BUILDER_BY_TARGET[target];

    if (_.isEmpty(targetDefinitions) || !builder) {
      return [];
    }

    const sections = builder({
      target,
      definitions: _.orderBy(targetDefinitions, ['name'], ['asc']),
      applicationId,
      subjectProperty,
      borrowers,
      properties,
      incomes,
      assets,
      liabilities,
      nbes,
    });

    if (_.isEmpty(sections)) {
      return [];
    }

    return [
      {
        key: `group:${target}`,
        target,
        groupHeader: CUSTOM_FIELD_TARGET_GROUP_LABEL[target],
        sections,
      },
    ];
  });
};

export const flattenSectionGroups = groups => _.flatMap(groups, group => group.sections);

export const getRequiredCustomFieldTargets = ({ definitions, selectedIds }) => {
  const selectedIdsSet = new Set(selectedIds);

  return _(definitions)
    .filter(definition => selectedIdsSet.has(definition?.id) && !_.isNil(definition?.target))
    .map('target')
    .uniq()
    .value();
};

const isSubmitValueEmpty = value => _.isNil(value) || value === '';

export const buildSubmitPayload = ({ sections, formValuesByKey }) =>
  _.flatMap(sections, section => {
    const sectionValues = formValuesByKey?.[section.key] ?? {};

    return _.map(section.definitions, definition => {
      const value = sectionValues[definition.id];

      return {
        definitionId: definition.id,
        targetEntityId: section.entityId,
        value: isSubmitValueEmpty(value) ? null : String(value),
      };
    });
  });
